import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { FormattedDate, FormattedDateTimeRange, FormattedMessage, useIntl } from 'react-intl';
import { IconButton, Tooltip } from '@mui/material';
import { LinkIcon } from '@synergy/theme-next';
import { PaloColumn, PaloFilterChips, PaloListView, PaloStatusPill, usePaloListSort } from '@synergy/components-palo';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { ReactElement, useEffect } from 'react';
import { UrlFilter } from '@synergy/components';
import { getDocumentStatusTranslationId, getLocalizedString } from '@synergy/utils';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import * as DemandActions from '../store/demands-overview-actions';
import { Contact } from '../../../../common/models/contact';
import { DemandOverview } from '../models/demand-overview';
import { PaloDemandsOverviewFilter } from './palo-demands-overview-filter';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { localesSelector } from '../../../../common/selectors/locale-selectors';
import { useSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

/** "Maier" for one, "Maier, +3" for several — as the legacy table showed them. */
const contactsLabel = (contacts: Contact[]): string => {
    if (0 === contacts.length) {
        return '';
    }

    return 1 === contacts.length ? contacts[0].fullName : `${contacts[0].fullName}, +${contacts.length - 1}`;
};

export const PaloDemandsOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    // The list opens in its own order, and asks the server for it: the header
    // showed "Nummer" with an arrow while the request carried no order at all,
    // so the oldest document stood on top under a header saying the newest
    // (LST-S16, #2234).
    const { sort, params: sortedParams } = usePaloListSort('demands');
    const { data, requesting } = useSelector(state => state.demands.overview);
    const locales = useSelector(localesSelector);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(DemandActions.fetchDemandOverview(sortedParams));

        return () => {
            dispatch(DemandActions.resetDemandOverview());
        };
    }, [dispatch, sortedParams]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const detailHref = (demand: DemandOverview): string => ROUTES.demands.detail.replace(':id', demand.id.toString());

    const copyLink = (demandId: number): void => {
        navigator.clipboard.writeText(`${window.location.origin}${ROUTES.demands.overview}/${demandId}`);
        dispatch(Snackbar.enqueue(PoolNotification.success('app.common.copiedLinkToClipboard')));
    };

    const columns: PaloColumn<DemandOverview>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            sortKey: 'number',
            cell: demand => demand.number,
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: demand => (
                <PaloStatusPill
                    status={demand.status}
                    label={<FormattedMessage id={getDocumentStatusTranslationId(demand.status)} />}
                />
            ),
        },
        {
            key: 'title',
            type: 'name',
            head: <FormattedMessage id="app.table.column.title" />,
            sortKey: 'title',
            cell: demand => getLocalizedString(locales, demand.title),
        },
        {
            key: 'customerContact',
            type: 'name',
            head: <FormattedMessage id="app.filter.customerContact" />,
            sortKey: 'customerContact',
            variant: 'muted',
            cell: demand => contactsLabel(demand.contacts),
        },
        {
            key: 'createdAt',
            type: 'date',
            head: <FormattedMessage id="app.table.column.publishDate" />,
            sortKey: 'createdAt',
            cell: demand => <FormattedDate value={demand.createdAt} year="numeric" month="short" day="2-digit" />,
        },
        {
            key: 'deliveryDateRange',
            type: 'date',
            head: <FormattedMessage id="app.demands.deliveryDateRange" />,
            cell: demand =>
                demand.deliveryDateFrom &&
                demand.deliveryDateTo && (
                    <FormattedDateTimeRange
                        day="2-digit"
                        month="short"
                        year="numeric"
                        from={dayjs(demand.deliveryDateFrom).toDate()}
                        to={dayjs(demand.deliveryDateTo).toDate()}
                    />
                ),
        },
        {
            key: 'endDate',
            type: 'date',
            head: <FormattedMessage id="app.table.column.endDate" />,
            sortKey: 'endDate',
            cell: demand => <FormattedDate value={demand.endDate} year="numeric" month="short" day="2-digit" />,
        },
        {
            key: 'link',
            type: 'icon',
            head: '',
            cell: demand => (
                <Tooltip title={<FormattedMessage id="app.demands.overview.copyLink" />} placement="top">
                    <IconButton
                        size="small"
                        onClick={event => {
                            // The row opens the demand; the button only copies.
                            event.stopPropagation();
                            copyLink(demand.id);
                        }}
                    >
                        <LinkIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <UrlFilter useLegacyFilter={true}>
            <PaloListView<DemandOverview>
                title={<FormattedMessage id="app.navigation.demands" />}
                filters={<PaloDemandsOverviewFilter />}
                filterChips={<PaloFilterChips />}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                columns={columns}
                items={data.items}
                getRowId={demand => demand.id}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                rowHref={detailHref}
                pagination={{
                    count: data.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            />
        </UrlFilter>
    );
};
